import React from 'react';
import { SailingDayTemplateFragments } from '@go-shared/types/sailing';
import { BubbleButton, BubbleBadge } from './BubbleUI';
import { BubbleButtonProps } from './BubbleUI/BubbleButton';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface TemplateGlanceButtonPropsBase {
  templateHistory?: SailingDayTemplateFragments;
  templateFragments?: SailingDayTemplateFragments;
  iconClassName?: string;
  locked: boolean;
}

interface FragmentStatusBlock {
  key: string;
  name: string;
  shortName: string;
  icon: IconName;
  variant: 'lime' | 'ecru' | 'smoke' | 'banana' | 'crimson' | 'grape' | 'ocean';
  textColor: string;
}

export type TemplateGlanceButtonProps = Omit<BubbleButtonProps, 'variant'> &
  TemplateGlanceButtonPropsBase;

export default function TemplateGlanceButton(props: TemplateGlanceButtonProps) {
  const {
    templateHistory,
    templateFragments,
    className: propClasses,
    iconClassName: propIconClasses,
    locked,
    ...rest
  } = props;

  const fragmentStatusBlocks: FragmentStatusBlock[] = [
    {
      key: 'el-1',
      name: 'Event',
      shortName: 'E',
      icon: locked
        ? 'lock'
        : templateFragments && templateFragments.events
        ? 'chain'
        : templateHistory && templateHistory.events
        ? 'chain-broken'
        : 'minus',
      variant:
        templateFragments && templateFragments.events
          ? 'lime'
          : templateHistory && templateHistory.events
          ? 'ecru'
          : 'smoke',
      textColor:
        templateFragments && templateFragments.events
          ? 'text-lime-dark'
          : templateHistory && templateHistory.events
          ? 'text-dirt'
          : 'text-slate-lightest',
    },
    {
      key: 'el-2',
      name: 'Venue',
      shortName: 'VD',
      icon: locked
        ? 'lock'
        : templateFragments && templateFragments.venueData
        ? 'chain'
        : templateHistory && templateHistory.venueData
        ? 'chain-broken'
        : 'minus',
      variant:
        templateFragments && templateFragments.venueData
          ? 'banana'
          : templateHistory && templateHistory.venueData
          ? 'ecru'
          : 'smoke',
      textColor:
        templateFragments && templateFragments.venueData
          ? 'text-banana-dark'
          : templateHistory && templateHistory.venueData
          ? 'text-dirt'
          : 'text-slate-lightest',
    },
    {
      key: 'el-3',
      name: 'Content',
      shortName: 'C',
      icon: locked
        ? 'lock'
        : templateFragments && templateFragments.content
        ? 'chain'
        : templateHistory && templateHistory.content
        ? 'chain-broken'
        : 'minus',
      variant:
        templateFragments && templateFragments.content
          ? 'ocean'
          : templateHistory && templateHistory.content
          ? 'ecru'
          : 'smoke',
      textColor:
        templateFragments && templateFragments.content
          ? 'text-ocean-dark'
          : templateHistory && templateHistory.content
          ? 'text-dirt'
          : 'text-slate-lightest',
    },
    {
      key: 'el-4',
      name: 'Print',
      shortName: 'P',
      icon: locked
        ? 'lock'
        : templateFragments && templateFragments.feed
        ? 'chain'
        : templateHistory && templateHistory.feed
        ? 'chain-broken'
        : 'minus',
      variant:
        templateFragments && templateFragments.feed
          ? 'grape'
          : templateHistory && templateHistory.feed
          ? 'ecru'
          : 'smoke',
      textColor:
        templateFragments && templateFragments.feed
          ? 'text-grape-dark'
          : templateHistory && templateHistory.feed
          ? 'text-dirt'
          : 'text-slate-lightest',
    },
  ];

  const classes = ['p-0-i text-left'];
  if (propClasses) classes.push(propClasses);

  const iconClasses = ['mr-2'];
  if (propIconClasses) iconClasses.push(propIconClasses);

  return (
    <BubbleButton
      {...rest}
      variant="white"
      inverse
      className={classes.join(' ')}
      style={{ borderColor: '#eeeeee' }}
    >
      <div>
        {fragmentStatusBlocks.map(fsb => (
          <BubbleBadge
            className="py-1 mr-1 whitespace-nowrap-i"
            key={fsb.key}
            variant={fsb.variant}
          >
            <FontAwesomeIcon fixedWidth className={iconClasses.join(' ')} icon={fsb.icon} />
            <span>{fsb.shortName}</span>
          </BubbleBadge>
        ))}
      </div>
    </BubbleButton>
  );
}
